import React, { useEffect, useState } from "react";
import axios from "axios";
import { apiUrl } from "../../config/config";

// Shared "device history" popup.
//
// Every page that shows a device serial (support patient list, partner patient
// list, and both device-details pages) reads the SAME source: the subscribe
// history returned by GET api/users/getSubsribe?mac_id=... That blob lists the
// patients the device has been handed to over time, so a single component
// serves all of them instead of each page re-implementing the fetch + parse.
//
// Props:
//   open    — render only when true
//   macId   — the device MAC to look up (required for a fetch)
//   serial  — serial number, shown in the header for context
//   theme   — "dark" (device pages) | "light" (partner patient list)
//   onClose — called on ✕, overlay click, or Escape
const DeviceHistoryModal = ({ open, macId, serial, theme = "dark", onClose }) => {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    const mac = String(macId || "").trim();
    if (!mac) {
      setText("");
      setError("No MAC id on this device — history unavailable.");
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError("");
    setText("");
    axios
      .get(`${apiUrl}api/users/getSubsribe`, { params: { mac_id: mac } })
      .then((res) => {
        if (cancelled) return;
        const raw = res.data && res.data.data;
        // The route JSON-parses the script output when it can and returns the
        // raw text otherwise — normalise both into something displayable.
        setText(
          typeof raw === "string"
            ? raw
            : raw != null
            ? JSON.stringify(raw, null, 2)
            : ""
        );
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("[DeviceHistory] failed:", err.message);
        setError("Could not load device history.");
        setLoading(false);
      });
    // Re-fetch whenever a different device is opened.
    return () => {
      cancelled = true;
    };
  }, [open, macId]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape" && onClose) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  // Strip the script's trailing status marker (as the support page does), then
  // drop blank lines so the entries read as a clean list.
  const lines = String(text || "")
    .replace(/\{"response":200\}/g, "")
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);

  const dark = theme !== "light";
  const c = dark
    ? {
        panel: "#0a1f20", border: "#1a5a62", head: "#2eaabb",
        body: "#c8dde0", muted: "#89a8c0", rule: "#132e30",
      }
    : {
        panel: "#ffffff", border: "#cdd7df", head: "#0a8aa0",
        body: "#1f2933", muted: "#5b6770", rule: "#eef2f5",
      };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
        zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: c.panel, border: `1px solid ${c.border}`, borderRadius: 10,
          padding: "18px 22px", width: 620, maxWidth: "94vw", maxHeight: "80vh",
          overflowY: "auto", color: c.body,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: c.head }}>
            Device History{serial ? " · " + serial : ""}
          </div>
          <button
            onClick={onClose}
            style={{ background: "none", border: "none", color: c.muted, fontSize: 18, cursor: "pointer" }}
          >
            ✕
          </button>
        </div>

        <div style={{ fontSize: 12, color: c.muted, marginBottom: 12, fontFamily: "ui-monospace, monospace" }}>
          MAC {macId || "—"}
        </div>

        {loading ? (
          <div style={{ fontSize: 13, color: c.muted, padding: "12px 0" }}>Loading…</div>
        ) : error ? (
          <div style={{ fontSize: 13, color: c.muted, padding: "12px 0" }}>{error}</div>
        ) : lines.length === 0 ? (
          <div style={{ fontSize: 13, color: c.muted, padding: "12px 0" }}>
            No previously connected patients recorded for this device.
          </div>
        ) : (
          <div style={{ fontSize: 13, lineHeight: 1.6 }}>
            {lines.map((item, key) => (
              <div
                key={key}
                style={{ padding: "7px 0", borderBottom: `1px solid ${c.rule}`, wordBreak: "break-word" }}
              >
                {item}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DeviceHistoryModal;
